"use client";

import React, { useEffect, useId, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import TurnstileField from "@/components/shared/TurnstileField";
import { REPLY_WINDOW } from "@/content/replyWindow";
import { useTurnstile } from "@/hooks/useTurnstile";
import { trackCheckoutPurchase } from "@/lib/analytics/checkout";
import { track } from "@/lib/analytics/track";
import { CONTACT_SEND_FAILURE } from "@/lib/contact/messages";
import {
  OFFER_LABELS,
  OFFER_INTERESTS,
  LOCAL_OFFERS,
  REPO_OFFERS,
  offerFormFieldsSchema,
  offerFromQuery,
  type OfferInterest,
} from "@/lib/contact/offer-schema";

const ADDRESS = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;

function visibleFormError(message: string | undefined): string {
  if (!message || ADDRESS.test(message)) return CONTACT_SEND_FAILURE;
  return message;
}

interface OfferContactFormProps {
  initialOffer?: OfferInterest;
}

type FieldErrors = Partial<Record<string, string>>;

function fieldError(errors: FieldErrors, name: string): string | undefined {
  return errors[name];
}

function fieldClass(errors: FieldErrors, name: string): string {
  return fieldError(errors, name) ? "field bad" : "field";
}

const panelStyle: React.CSSProperties = { background: "#0f172a", padding: "clamp(24px, 4vw, 40px)" };

function Required() {
  return (
    <span className="req" aria-hidden="true">
      {" "}*
    </span>
  );
}

function Optional() {
  return <span className="hint"> (optional)</span>;
}

export default function OfferContactForm({ initialOffer }: OfferContactFormProps) {
  const searchParams = useSearchParams();
  const offerParam = searchParams.get("offer");
  const formId = useId();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [offer, setOffer] = useState<OfferInterest>(
    initialOffer ?? offerFromQuery(offerParam),
  );
  const [message, setMessage] = useState("");
  const [repoUrl, setRepoUrl] = useState("");
  const [serviceArea, setServiceArea] = useState("");
  const [currentWebsite, setCurrentWebsite] = useState("");
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [website, setWebsite] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const {
    isVisible,
    token,
    message: turnstileMessage,
    state: turnstileState,
    containerRef,
    handleError,
    handleExpire,
    handleVerify,
    requestVerification,
  } = useTurnstile({
    requiredMessage: "Running a quick security check. Submit again in a moment.",
  });

  useEffect(() => {
    setOffer(initialOffer ?? offerFromQuery(offerParam));
  }, [initialOffer, offerParam]);

  const checkoutSucceeded = searchParams.get("checkout") === "success";
  const checkoutSessionId = searchParams.get("session_id");
  useEffect(() => {
    if (checkoutSucceeded) trackCheckoutPurchase(checkoutSessionId);
  }, [checkoutSucceeded, checkoutSessionId]);

  const showRepo = REPO_OFFERS.includes(offer);
  const showLocal = LOCAL_OFFERS.includes(offer);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    const payload = {
      name,
      email,
      company,
      phone,
      offer,
      message,
      repoUrl: showRepo ? repoUrl : "",
      serviceArea: showLocal ? serviceArea : "",
      currentWebsite: showLocal ? currentWebsite : "",
      consentAccepted,
      website,
      utmSource: searchParams.get("utm_source") ?? "",
      utmMedium: searchParams.get("utm_medium") ?? "",
      utmCampaign: searchParams.get("utm_campaign") ?? "",
      utmTerm: searchParams.get("utm_term") ?? "",
      utmContent: searchParams.get("utm_content") ?? "",
      referrer: typeof document !== "undefined" ? document.referrer : "",
      page:
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.search}`
          : "",
    };

    const validation = offerFormFieldsSchema.safeParse(payload);
    if (!validation.success) {
      const nextErrors: FieldErrors = {};
      for (const issue of validation.error.issues) {
        const key = String(issue.path[0] ?? "form");
        if (!nextErrors[key]) nextErrors[key] = issue.message;
      }
      setErrors(nextErrors);
      const firstInvalid = validation.error.issues[0]?.path[0];
      if (firstInvalid) {
        document.getElementById(`${formId}-${String(firstInvalid)}`)?.focus();
      }
      track("offer_form_error", { offer, error_type: "validation" });
      return;
    }

    if (!token) {
      requestVerification();
      setFormError("Finish the security check, then send the form again.");
      track("offer_form_error", { offer, error_type: "turnstile" });
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          formType: "offer",
          turnstileToken: token,
          formData: validation.data,
        }),
      });
      const result = (await response.json().catch(() => ({}))) as {
        error?: string;
        code?: string;
        fieldErrors?: Record<string, string[] | undefined>;
      };

      if (!response.ok) {
        const nextErrors: FieldErrors = {};
        if (result.fieldErrors) {
          for (const [key, messages] of Object.entries(result.fieldErrors)) {
            if (messages && messages[0]) nextErrors[key] = messages[0];
          }
        }
        setErrors(nextErrors);
        setFormError(visibleFormError(result.error));
        const errorType =
          result.code === "TURNSTILE_REQUIRED" || result.code === "TURNSTILE_FAILED" ? "turnstile" : "server";
        track("offer_form_error", { offer, error_type: errorType });
        return;
      }

      track("offer_form_submit", {
        offer,
        form_location: "contact",
        has_repo_url: Boolean(validation.data.repoUrl?.trim()),
      });
      setSucceeded(true);
    } catch {
      setFormError(CONTACT_SEND_FAILURE);
      track("offer_form_error", { offer, error_type: "server" });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (succeeded) {
    return (
      <div className="panel topbar" style={panelStyle}>
        <div className="stack g20 panel-in items-start" style={{ minHeight: 420 }} role="status">
          <span className="tile" style={{ width: 56, height: 56, borderRadius: 16 }}>
            <svg aria-hidden="true" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
          <h2 className="h3">Message sent</h2>
          <p className="body">Thanks. I&#8217;ll review it within {REPLY_WINDOW} and reply with next steps.</p>
          <ol className="dots">
            <li>I review your note</li>
            <li>If it fits, we set up a call</li>
            <li>You get a written proposal or a clear no</li>
          </ol>
          <Link className="btn btn-outline" href="/">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="panel topbar" style={panelStyle}>
      <form className="stack g20 relative" noValidate onSubmit={onSubmit} aria-label="Contact form">
        {checkoutSucceeded ? (
          <div className="alert alert-info" role="status">
            Thanks for your order. Your receipt comes by email once the payment clears. Tell me about the project below.
          </div>
        ) : null}
        {turnstileState === "required" ? (
          <div className="alert alert-info" role="status">
            Running a quick security check. Submit again in a moment.
          </div>
        ) : null}
        {formError ? (
          <div className="alert alert-err" role="alert">
            {formError}
          </div>
        ) : null}

        <div className="field-wrap">
          <label className="label" htmlFor={`${formId}-offer`}>
            What&apos;s this about?
            <Required />
          </label>
          <select
            id={`${formId}-offer`}
            name="offer"
            required
            value={offer}
            onChange={(event) => setOffer(event.target.value as OfferInterest)}
            className="field"
            aria-describedby={`${formId}-offer-hint`}
          >
            {OFFER_INTERESTS.map((value) => (
              <option key={value} value={value}>
                {OFFER_LABELS[value]}
              </option>
            ))}
          </select>
          <span id={`${formId}-offer-hint`} className="hint">
            Links on each offer page pick this for you.
          </span>
        </div>

        <div className="grid-2" style={{ gap: 16 }}>
          <div className="field-wrap">
            <label className="label" htmlFor={`${formId}-name`}>
              Name
              <Required />
            </label>
            <input
              id={`${formId}-name`}
              name="name"
              autoComplete="name"
              required
              value={name}
              aria-invalid={Boolean(fieldError(errors, "name"))}
              aria-describedby={fieldError(errors, "name") ? `${formId}-name-error` : undefined}
              onChange={(event) => setName(event.target.value)}
              className={fieldClass(errors, "name")}
            />
            {fieldError(errors, "name") ? (
              <span id={`${formId}-name-error`} className="err" role="alert">
                {fieldError(errors, "name")}
              </span>
            ) : null}
          </div>

          <div className="field-wrap">
            <label className="label" htmlFor={`${formId}-email`}>
              Email
              <Required />
            </label>
            <input
              id={`${formId}-email`}
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              aria-invalid={Boolean(fieldError(errors, "email"))}
              aria-describedby={fieldError(errors, "email") ? `${formId}-email-error` : undefined}
              onChange={(event) => setEmail(event.target.value)}
              className={fieldClass(errors, "email")}
            />
            {fieldError(errors, "email") ? (
              <span id={`${formId}-email-error`} className="err" role="alert">
                {fieldError(errors, "email")}
              </span>
            ) : null}
          </div>
        </div>

        <div className="grid-2" style={{ gap: 16 }}>
          <div className="field-wrap">
            <label className="label" htmlFor={`${formId}-company`}>
              Company website or company
              <Optional />
            </label>
            <input
              id={`${formId}-company`}
              name="company"
              autoComplete="organization"
              value={company}
              onChange={(event) => setCompany(event.target.value)}
              className="field"
            />
          </div>

          <div className="field-wrap">
            <label className="label" htmlFor={`${formId}-phone`}>
              Phone
              <Optional />
            </label>
            <input
              id={`${formId}-phone`}
              name="phone"
              type="tel"
              autoComplete="tel"
              value={phone}
              aria-invalid={Boolean(fieldError(errors, "phone"))}
              aria-describedby={fieldError(errors, "phone") ? `${formId}-phone-error` : undefined}
              onChange={(event) => setPhone(event.target.value)}
              className={fieldClass(errors, "phone")}
            />
            {fieldError(errors, "phone") ? (
              <span id={`${formId}-phone-error`} className="err" role="alert">
                {fieldError(errors, "phone")}
              </span>
            ) : null}
          </div>
        </div>

        {showRepo ? (
          <div className="field-wrap panel-in">
            <label className="label" htmlFor={`${formId}-repoUrl`}>
              Repo URL
              <Optional />
            </label>
            <input
              id={`${formId}-repoUrl`}
              name="repoUrl"
              type="url"
              inputMode="url"
              placeholder="https://github.com/you/app"
              value={repoUrl}
              aria-invalid={Boolean(fieldError(errors, "repoUrl"))}
              aria-describedby={`${formId}-repoUrl-hint${fieldError(errors, "repoUrl") ? ` ${formId}-repoUrl-error` : ""}`}
              onChange={(event) => setRepoUrl(event.target.value)}
              className={fieldClass(errors, "repoUrl")}
            />
            <span id={`${formId}-repoUrl-hint`} className="hint">
              You can share access later. Don&#8217;t paste keys or passwords here.
            </span>
            {fieldError(errors, "repoUrl") ? (
              <span id={`${formId}-repoUrl-error`} className="err" role="alert">
                {fieldError(errors, "repoUrl")}
              </span>
            ) : null}
          </div>
        ) : null}

        {showLocal ? (
          <div className="grid-2 panel-in" style={{ gap: 16 }}>
            <div className="field-wrap">
              <label className="label" htmlFor={`${formId}-serviceArea`}>
                Service area
              </label>
              <input
                id={`${formId}-serviceArea`}
                name="serviceArea"
                placeholder="Omaha, Papillion"
                value={serviceArea}
                onChange={(event) => setServiceArea(event.target.value)}
                className="field"
              />
            </div>
            <div className="field-wrap">
              <label className="label" htmlFor={`${formId}-currentWebsite`}>
                Current website
                <Optional />
              </label>
              <input
                id={`${formId}-currentWebsite`}
                name="currentWebsite"
                type="url"
                inputMode="url"
                value={currentWebsite}
                aria-invalid={Boolean(fieldError(errors, "currentWebsite"))}
                aria-describedby={
                  fieldError(errors, "currentWebsite") ? `${formId}-currentWebsite-error` : undefined
                }
                onChange={(event) => setCurrentWebsite(event.target.value)}
                className={fieldClass(errors, "currentWebsite")}
              />
              {fieldError(errors, "currentWebsite") ? (
                <span id={`${formId}-currentWebsite-error`} className="err" role="alert">
                  {fieldError(errors, "currentWebsite")}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="field-wrap">
          <label className="label" htmlFor={`${formId}-message`}>
            Message
            <Required />
          </label>
          <textarea
            id={`${formId}-message`}
            name="message"
            required
            rows={6}
            value={message}
            placeholder="What do you need help with?"
            aria-invalid={Boolean(fieldError(errors, "message"))}
            aria-describedby={fieldError(errors, "message") ? `${formId}-message-error` : undefined}
            onChange={(event) => setMessage(event.target.value)}
            className={fieldClass(errors, "message")}
          />
          {fieldError(errors, "message") ? (
            <span id={`${formId}-message-error`} className="err" role="alert">
              {fieldError(errors, "message")}
            </span>
          ) : null}
        </div>

        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: -10000,
            width: 1,
            height: 1,
            overflow: "hidden",
          }}
        >
          <label htmlFor={`${formId}-website`}>Company website</label>
          <input
            id={`${formId}-website`}
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(event) => setWebsite(event.target.value)}
            style={{
              position: "absolute",
              left: -10000,
              width: 1,
              height: 1,
              minWidth: 0,
              maxWidth: 1,
              minHeight: 0,
              maxHeight: 1,
              padding: 0,
              border: 0,
              overflow: "hidden",
            }}
          />
        </div>

        <div className="stack g8">
          <label className="consent" htmlFor={`${formId}-consentAccepted`}>
            <input
              id={`${formId}-consentAccepted`}
              name="consentAccepted"
              type="checkbox"
              checked={consentAccepted}
              onChange={(event) => setConsentAccepted(event.target.checked)}
              aria-invalid={Boolean(fieldError(errors, "consentAccepted"))}
              aria-describedby={
                fieldError(errors, "consentAccepted")
                  ? `${formId}-consent-error`
                  : `${formId}-consent-hint`
              }
            />
            <span id={`${formId}-consent-hint`}>
              I agree to the{" "}
              <Link
                className="inline-flex min-h-11 items-center underline underline-offset-2"
                href="/privacy-statement"
              >
                Privacy Statement
              </Link>{" "}
              and to being contacted about this
              request.
              <Required />
            </span>
          </label>
          {fieldError(errors, "consentAccepted") ? (
            <span id={`${formId}-consent-error`} className="err" role="alert">
              {fieldError(errors, "consentAccepted")}
            </span>
          ) : null}
        </div>

        <TurnstileField
          action="contact-offer"
          containerRef={containerRef}
          id={`${formId}-turnstile`}
          message={turnstileState === "required" ? null : turnstileMessage}
          onError={handleError}
          onExpire={handleExpire}
          onVerify={handleVerify}
          state={turnstileState}
          visible={isVisible || turnstileState === "idle" || turnstileState === "verified"}
        />

        <button type="submit" disabled={isSubmitting} className="btn btn-primary btn-block">
          {isSubmitting ? (
            <>
              <span className="spinner" aria-hidden="true" />
              Sending...
            </>
          ) : (
            "Send message"
          )}
        </button>
        <p className="caption text-center">
          This form is the only way to reach me. I reply within {REPLY_WINDOW}.
        </p>
      </form>
    </div>
  );
}
